import { Link } from 'expo-router';
import { useState } from 'react';
import {
    BodyMediumRegular,
    BodySmallEmphasized,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    Edit,
    FruitfulCustomIcon,
    FullScreenSpinner,
    IconButton,
    RowHeader,
    Sheet,
    useThemeColors,
    useToastController,
    YStack,
} from 'smoothie';

import { type User } from '../../../user/types/User';
import { useRemoveBeneficiary } from '../../hooks/beneficiaries/useRemoveBeneficiary';
import { useUpdateBeneficiary } from '../../hooks/beneficiaries/useUpdateBeneficiary';
import { RelationshipStatus } from '../../types/BankAccount';
import { type AccountDetailWithBeneficiaries } from '../../types/Beneficiaries';
import { POLICY_DISCLOSURES, PolicyType } from '../Disclosures';
import { ManageAccountBeneficiaries } from './ManageAccountBeneficiaries';

const EditButton = ({ onPress }: { onPress: () => void }) => {
    return <IconButton variant="secondary" icon={Edit} onPress={onPress} />;
};

export const Overview = ({
    user,
    bankAccountDetails,
    isJointMembership,
    onPress,
    onUpdate,
}: {
    user: User;
    bankAccountDetails: AccountDetailWithBeneficiaries[];
    isJointMembership: boolean;
    onPress: () => void;
    onUpdate: () => void;
}) => {
    const partner = user.partnerMember;
    const names = new Map<string, string>();
    names.set(user.id, 'You');
    if (partner?.id != null) {
        names.set(partner.id, partner.firstName ?? '');
    }

    const [selectedAccount, setSelectedAccount] =
        useState<AccountDetailWithBeneficiaries | null>(null);

    const toast = useToastController();
    const { mutateAsync: removeBeneficiary, isPending: isRemoving } =
        useRemoveBeneficiary();
    const { mutateAsync: updateBeneficiary, isPending: isUpdating } =
        useUpdateBeneficiary();

    const { getColor } = useThemeColors();

    if (isRemoving || isUpdating) {
        return <FullScreenSpinner />;
    }

    const onSave = async (
        updates: Array<{
            id: string;
            bankAccountId: string;
            percentage: number;
        }>,
    ) => {
        await updateBeneficiary(
            {
                body: updates,
            },
            {
                onError: () => {
                    toast.show('Could not update');
                },
                onSuccess: () => {
                    onUpdate();
                    toast.show('Updated!');
                },
                onSettled: () => {
                    setSelectedAccount(null);
                },
            },
        );
    };

    const onDelete = async (id: string) => {
        if (selectedAccount == null) {
            return;
        }

        await removeBeneficiary(
            {
                body: {
                    id,
                    bankAccountIds: [selectedAccount.bankAccountId],
                },
            },
            {
                onError: () => {
                    toast.show('Could not remove beneficiary');
                },
                onSuccess: () => {
                    onUpdate();
                    toast.show('Beneficiary removed');
                },
                onSettled: () => {
                    setSelectedAccount(null);
                },
            },
        );
    };

    const onDeleteFromAllAccounts = async (id: string) => {
        const bankAccountIds =
            bankAccountDetails.map((d) => d.bankAccountId) ?? [];
        await removeBeneficiary(
            {
                body: {
                    id,
                    bankAccountIds,
                },
            },
            {
                onError: () => {
                    toast.show('Could not remove beneficiary');
                },
                onSuccess: () => {
                    onUpdate();
                    toast.show('Beneficiary removed');
                },
                onSettled: () => {
                    setSelectedAccount(null);
                },
            },
        );
    };

    const instructions = 'Each account may have up to 4 beneficiaries.';

    return (
        <YStack width="100%">
            <BodyMediumRegular color="$gray1000">
                Beneficiaries are the individual(s) you designate to receive the
                funds in your accounts after you pass away. Please see your{' '}
                <Link
                    href={
                        POLICY_DISCLOSURES[PolicyType.DEPOSIT_ACCOUNT_AGREEMENT]
                            .url
                    }
                    style={{
                        color: getColor('$gray1000'),
                    }}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <BodyMediumRegular textDecorationLine="underline">
                        Fruitful Deposit Account Agreement
                    </BodyMediumRegular>
                </Link>{' '}
                for more information.
            </BodyMediumRegular>
            {bankAccountDetails.map((detail) => {
                const isJointAccount =
                    detail.jointUserRelationshipStatus ===
                    RelationshipStatus.ACTIVE;

                const label = isJointMembership
                    ? isJointAccount
                        ? 'Joint account'
                        : 'Individual account'
                    : '';

                const showEditButton = detail.beneficiaries.length > 0;

                const onEdit = () => {
                    setSelectedAccount(detail);
                };

                return (
                    <YStack paddingTop={16} key={detail.accountNumber}>
                        <RowHeader
                            borderTopLeftRadius={24}
                            borderTopRightRadius={24}
                            leftContentOverride={
                                <FruitfulCustomIcon
                                    icon={detail.icon}
                                    color={detail.color}
                                    size="xs"
                                />
                            }
                            {...(showEditButton
                                ? {
                                      rightContent: (
                                          <EditButton onPress={onEdit} />
                                      ),
                                  }
                                : {})}
                            title={detail.nickname ?? 'Cash account'}
                            label={label}
                            marginBottom={0}
                        />
                        {detail.beneficiaries.length > 0 ? (
                            detail.beneficiaries
                                .sort(
                                    (a, b) =>
                                        (b.ownershipPercentage ?? 0) -
                                            (a.ownershipPercentage ?? 0) ||
                                        a.legalFirstName.localeCompare(
                                            b.legalFirstName,
                                        ),
                                )
                                .map((b, index) => {
                                    const isLast =
                                        index ===
                                        detail.beneficiaries.length - 1;
                                    return (
                                        <RowHeader
                                            key={b.id}
                                            backgroundColor="$gray100"
                                            marginBottom={0}
                                            borderBottomLeftRadius={
                                                isLast ? 24 : 0
                                            }
                                            borderBottomRightRadius={
                                                isLast ? 24 : 0
                                            }
                                            title={`${b.legalFirstName} ${
                                                b.legalLastName
                                            }`}
                                            rightContent={
                                                <BodySmallEmphasized color="$black">
                                                    {`${b.ownershipPercentage ?? 0}%`}
                                                </BodySmallEmphasized>
                                            }
                                            titleProps={{ color: '$black' }}
                                            subtitle={`Added by ${names.get(b.addedByUserId)}`}
                                        />
                                    );
                                })
                        ) : (
                            <BodySmallRegular
                                color="$gray1000"
                                backgroundColor="$gray0"
                                padding={24}
                                borderBottomLeftRadius={24}
                                borderBottomRightRadius={24}
                            >
                                No added beneficiaries yet
                            </BodySmallRegular>
                        )}
                    </YStack>
                );
            }) ?? <BodyMediumRegular>No accounts!</BodyMediumRegular>}
            <Button
                marginTop={24}
                text="Add a beneficiary"
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.LARGE}
                onPress={onPress}
            />
            <BodySmallRegular
                paddingVertical={24}
                textAlign="center"
                color="$gray1000"
            >
                {instructions}
            </BodySmallRegular>
            <Sheet
                isOpen={selectedAccount != null}
                showCloseButton={false}
                onClose={() => {
                    setSelectedAccount(null);
                }}
            >
                {selectedAccount &&
                    selectedAccount.beneficiaries.length > 0 && (
                        <ManageAccountBeneficiaries
                            user={user}
                            detail={selectedAccount}
                            isSaving={isUpdating}
                            onSave={onSave}
                            onDelete={onDelete}
                            onDeleteFromAllAccounts={onDeleteFromAllAccounts}
                        />
                    )}
            </Sheet>
        </YStack>
    );
};
